<script lang="ts">
  import { t } from '$lib/translations'
  import { TabsBookingTypes, BookingTab, MyBookingTab, AtDisposalTab } from '$components/BookingsTabs'
  import { Armchair, BusFront, PartyPopper } from 'lucide-svelte'
  import type { TabItem } from '$lib/types'
  import { title } from '$lib/stores/title'
  import Carousel from '$components/Carousel.svelte'
  import NotFoundBooking from '$components/NotFoundBooking.svelte'
  import AdvantageCard from '$components/AdvantageCard.svelte'
  import NewOfficesBanner from '$components/NewOfficesBanner/NewOfficesBanner.svelte'
  import type { ActionData, PageData } from './$types'
  import { onMount } from 'svelte'
  import { getDefaultPurchaseInfoData, getGtagEventData, getSlideImages, gtagEvent, scrollIntoView } from '$lib/helpers'
  import { twMerge } from 'tailwind-merge'
  import ServicePriceCard from '$components/ServicePriceCard.svelte'
  import MyBookingComponent from '$components/MyBookingComponent.svelte'
  import { showLoading } from '$lib/stores/layout'
  import { purchaseInfo, purchaseInfoReturn, setPurchaseInfo } from '$lib/stores/booking-info'
  import { SERVICE_TYPES_NAMES } from '$lib/constants'
  import ServicePriceCardUnavailable from '$components/ServicePriceCardUnavailable.svelte'
  import { Button } from '$lib/components/ui/button'
  import { Skeleton } from '$lib/components/ui/skeleton'
  import HomeCoupon from '$components/HomeCoupon.svelte'
  import ServicesSection from '$components/ServicesSection.svelte'
  import { browser } from '$app/environment'

  export let form: ActionData
  export let data: PageData

  title.clear()

  const items: TabItem[] = [
    { label: 'common.book_your_transfer', labelIcon: BusFront, value: 1, component: BookingTab, showMobile: true },
    { label: 'common.at_disposal', labelIcon: BusFront, value: 2, component: AtDisposalTab, showMobile: true },
    { label: 'common.my_booking', labelIcon: Armchair, value: 3, component: MyBookingTab, showMobile: true }
    // TODO: Hidden until it is implemented
    // { label: 'common.lastminute', labelIcon: Hourglass, value: 4, component: LastMinuteTab },
    // { label: 'common.events', labelIcon: PartyPopper, value: 5, component: EventsTab }
  ]

  let hasFormResponse = false
  let activeTabValue = 1
  let loadingCoupons = false

  onMount(() => {
    if (data.recent) {
      $showLoading = false
    }

    if (data.showCookiesBanner && browser && 'Didomi' in window) {
      window.Didomi.notice.show()
    }

    if (data.myBooking) {
      form = { myBooking: data.myBooking } as ActionData
    }

    if (form) {
      hasFormResponse = true

      if (hasFormResponse && !form.availability && !form.availableServices) {
        activeTabValue = 1
        scrollIntoView('#parent')
      }

      if (form.availableServices) {
        activeTabValue = 1
        scrollIntoView('#available-services')

        const info = getDefaultPurchaseInfoData(data.user, form.selectedInfo, form.availableServices)

        setPurchaseInfo(info)

        if (typeof window !== 'undefined' && 'gtag' in window) {
          const eventoGAData = getGtagEventData(1, $purchaseInfo, $purchaseInfoReturn)
          gtagEvent('eventoGA', eventoGAData)
        }
      }

      if (form.myBooking || typeof form.myBooking === 'object') {
        activeTabValue = 3
        scrollIntoView('#my-booking')
      }
    }
  })

  $: if (!form) {
    hasFormResponse = false
    activeTabValue = 1
  }

  $: if (form?.availableServices && !loadingCoupons && $showLoading) {
    $showLoading = false
  }

  $: userIsLogged = data.authUser?.id !== undefined

  function convertServiceType(type: string) {
    return SERVICE_TYPES_NAMES[type as keyof typeof SERVICE_TYPES_NAMES]
  }

  let y: number

  $: isStickyActive = y > 330 && !form?.availableServices
</script>

<svelte:head>
  <title>{$title}</title>
</svelte:head>

<svelte:window bind:scrollY={y} />

<div class="hidden lg:block p-10 z-10 content-center text-white text-pretty [text-shadow:_2px_5px_5px_rgb(0_0_0_/_40%)]">
  <h1 class="text-[40px] font-bold lg:leading-loose">{$t('home.airport_transfer_leader')}</h1>
  <p class="text-[33px]">{$t('home.shared_services_and_private_services', {shuttleFromPrice: data.cleanedServicePrices["shuttle"] || "7€" , privateFromPrice: data.cleanedServicePrices["private"] || "42€"})}</p>
</div>

<TabsBookingTypes
  {items}
  {activeTabValue}
  {isStickyActive}
  class={twMerge(
    'lg:top-0 lg:left-0 w-full lg:px-10 z-50 lg:h-[184px] transition-all duration-[400ms]',
    form?.availableServices ? '' : 'lg:sticky',
    isStickyActive ? 'lg:px-0' : ''
  )}
/>
<!-- This is equal to height of image - 224px -->
<div class="lg:h-[190px]" />

<section class="w-full overflow-hidden lg:absolute justify-center pt-2 lg:p-0 lg:h-[540px]">
  {#await getSlideImages()}
    <Skeleton class="w-full h-full" />
  {:then images}
    <Carousel {images} duration={9000} showControls={true} class="relative hidden lg:flex " />
  {:catch}
    <Carousel images={['/slide_2.jpg']} duration={9000} showControls={true} class="relative hidden lg:flex " />
  {/await}

  {#if form && 'myBooking' in form && form.myBooking === null}
    <div class="flex w-full items-center relative lg:absolute lg:top-[35%] p-2 lg:px-10 lg:my-28 justify-center">
      <NotFoundBooking
        title={$t('errors.not_found_booking_title')}
        description={$t('errors.' + form?.myBookingError)}
        icon={BusFront}
        class="w-[80%] bg-opacity-90 border-solid border-red-700 shadow-xl"
      />
    </div>
  {/if}
</section>

{#if form?.availableServices}
  <section id="available-services" class="relative flex flex-col w-full gap-5 p-5 items-center">
    <HomeCoupon bind:loadingCoupons availableServices={form?.availableServices} selectedInfo={form.selectedInfo} user={data?.user} />

    <h1 class="text-5xl text-center my-9">{$t('home.choose_option')}</h1>
    <div class=" grid grid-cols-2 lg:flex lg:flex-col lg:items-center gap-5">
      {#key loadingCoupons}
        {#each form.availableServices as service}
          <ServicePriceCard
            serviceType={convertServiceType(service.serviceType)}
            vehicleType={service.vehicleType}
            maxPax={service.pax}
            price={service.price}
            discountAmount={service.discountAmount}
            discountType={service.discountType}
            priceWithFixedRate={service.priceWithFixedRate}
            startMeetingPoint={service.startMeetingPoint}
            endMeetingPoint={service.endMeetingPoint}
            bookerPlacesForShuttle={form.selectedInfo.bookerPlacesForShuttle}
          />
        {/each}
      {/key}
      {#each form.finalUnAvailableServices as service}
        <ServicePriceCardUnavailable serviceType={service.serviceType} vehicleType={service.vehicleType} />
      {/each}
    </div>
  </section>
{/if}

{#if hasFormResponse && !form?.availableServices && !form?.availability && activeTabValue !== 3}
  <section id="no-availability" class="relative flex flex-col w-full gap-5 p-5 items-center">
    <div class="w-full flex justify-center items-center p-9">
      <div class="max-w-xl flex flex-col justify-center items-center text-center gap-5">
        <div class="flex flex-col items-center gap-1">
          <h1 class="text-3xl font-semibold text-cs-orange">{$t('home.no_availability')}</h1>
          <p class="mt-10 text-xl">
            {$t('home.no_availability_message')}
          </p>
        </div>
      </div>
    </div>
  </section>
{/if}

{#if form?.myBooking}
  <section id="my-booking" class="relative py-5">
    <MyBookingComponent booking={form.myBooking} {userIsLogged} loggedUserEmail={data.authUser?.email} showTitle={false} />
  </section>
{/if}

<section
  class={twMerge('hidden bg-white relative lg:flex flex-col justify-around text-center h-[400px]', hasFormResponse ? 'lg:hidden' : '')}
>
  <h1 class="text-5xl mb-0 font-bold">
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html $t('home.why_book_with_us')}
  </h1>
  <div class="flex flex-row w-full text-cs-orange justify-center gap-14">
    <AdvantageCard icon={'/piggy.svg'} title={$t('common.economic')} content={$t('home.enjoy_transfer_service_low_price')} />
    <AdvantageCard icon={'/clock.svg'} title={$t('common.fast')} content={$t('home.24hattention_description')} />
    <AdvantageCard icon={'/24hattention.svg'} title={$t('common.24hattention')} content={$t('home.fast_description')} />
  </div>
</section>
<section
  class={twMerge(
    'hidden  relative lg:flex flex-col justify-evenly text-center h-[540px] bg-[#F3F3F3] ',
    hasFormResponse ? 'lg:hidden' : ''
  )}
>
  <ServicesSection servicesPrices={data.cleanedServicePrices}></ServicesSection>
</section>
<section
  id="events-section"
  class={twMerge('hidden lg:flex w-full h-[540px] relative p-28 text-white items-end', hasFormResponse ? 'lg:hidden' : '')}
>
  <div class="w-2/3">
    <h1 class="text-6xl font-semibold [text-shadow:_2px_5px_5px_rgb(0_0_0_/_40%)]">{$t('home.we_take_you_to_all_events')}</h1>
    <Button
      class="rounded-none transition-all group hover:bg-green-600 h-12 flex bg-green-700 uppercase my-12 px-0 text-white w-fit"
      href="https://lanzadera.canaryshuttle.com/"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span class="transition-all group-hover:px-6 flex-grow px-8">{$t('home.check_out_the_events')}</span>
      <span class="transition-all group-hover:px-6 bg-green-500 px-4 py-[14px] h-12 text-gray-900">
        <PartyPopper size={18} strokeWidth={2} color={'white'} />
      </span>
    </Button>
    <p class="text-sm">{$t('home.check_wich_events_are_taking_place')}</p>
  </div>
</section>

<NewOfficesBanner class={twMerge('hidden lg:flex w-full xl:h-[540px] lg:h-[680px]', hasFormResponse ? 'lg:hidden' : '')} />

<style>
  #events-section {
    background-image: url('/events-background-2.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: top;
  }
</style>
